import React, { useState, useRef, useEffect } from 'react';
import { CloseIcon, CartIcon } from './Icons';
import MenuDrawer from './MenuDrawer';
import AuthModal from './AuthModal';
import Footer from './Footer';
import BookingDrawer from './BookingDrawer';
import Header from './Header';
import { triggerStockLimitAlert } from '../utils/firebaseService';

export default function Collections({
  view,
  setView,
  isMenuOpen,
  setIsMenuOpen,
  isCartOpen,
  setIsCartOpen,
  isBookingOpen,
  setIsBookingOpen,
  isSearchOpen,
  setIsSearchOpen,
  favorites,
  setFavorites,
  cartItems,
  setCartItems,
  bookingStep,
  setBookingStep,
  bookingForm,
  setBookingForm,
  products,
  setProducts,
  selectedCollectionId,
  setSelectedCollectionId,
  setSelectedProduct,
  currentUser,
  setCurrentUser,
  collectionsData,
}) {

  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [addedProductIds, setAddedProductIds] = useState({});
  const productSectionRef = useRef(null);



  const collectionProducts = {};

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCollectionId]);

  useEffect(() => {
    const scrollToSection = (smooth = true) => {
      const el = document.getElementById('collections-content');
      if (el) {
        const targetTop = el.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({
          top: Math.round(targetTop),
          behavior: smooth ? 'smooth' : 'auto'
        });
      }
    };

    const shouldSkip = sessionStorage.getItem('hok_skip_collection_hero');
    if (shouldSkip === 'true') {
      sessionStorage.removeItem('hok_skip_collection_hero');
      scrollToSection(false);
      const t1 = setTimeout(() => scrollToSection(false), 50);
      const t2 = setTimeout(() => scrollToSection(true), 150);
      const t3 = setTimeout(() => scrollToSection(true), 350);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
        clearTimeout(t3);
      };
    }
  }, []);

  useEffect(() => {
    const handleScrollEvent = () => {
      const el = document.getElementById('collections-content');
      if (el) {
        const targetTop = el.getBoundingClientRect().top + window.pageYOffset;
        window.scrollTo({
          top: Math.round(targetTop),
          behavior: 'smooth'
        });
      }
    };
    window.addEventListener('hok_scroll_to_collection_content', handleScrollEvent);
    return () => window.removeEventListener('hok_scroll_to_collection_content', handleScrollEvent);
  }, []);

  const handlePageChange = (page) => {
    const pageCount = getPageCount();
    if (page < 1 || page > pageCount) return;
    setCurrentPage(page);
    if (productSectionRef.current) {
      const headerOffset = 160;
      const elementPosition = productSectionRef.current.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  const getPageProducts = () => {
    const collId = selectedCollectionId || 1;
    const collName = collectionsData[collId]?.title;

    const isCleared = localStorage.getItem('hok_products_cleared') === 'true';
    const deletedIds = (() => {
      try {
        return new Set(JSON.parse(localStorage.getItem('hok_deleted_products') || '[]'));
      } catch (e) {
        return new Set();
      }
    })();

    const defaultItems = isCleared ? [] : (collectionProducts[collId] || [])
      .filter(item => !deletedIds.has(item.id))
      .map(item => ({ stock: 10, ...item }));

    const databaseItems = (products || [])
      .filter(p => p.collection === collName && !deletedIds.has(p.id));

    const combined = [...databaseItems];
    defaultItems.forEach(item => {
      const dbMatch = (products || []).find(p => String(p.id) === String(item.id));
      if (dbMatch) {
        if (!combined.some(x => String(x.id) === String(item.id))) {
          combined.push(dbMatch);
        }
      } else if (!combined.some(x => String(x.id) === String(item.id))) {
        combined.push(item);
      }
    });
    // Ensure all items have live stock mapped from products state
    const liveItems = combined.map(p => {
      const live = (products || []).find(x => String(x.id) === String(p.id));
      return live ? { ...p, ...live } : p;
    });
    const startIndex = (currentPage - 1) * 8;
    return liveItems.slice(startIndex, startIndex + 8);
  };

  const getPageCount = () => {
    const collId = selectedCollectionId || 1;
    const collName = collectionsData[collId]?.title;

    const isCleared = localStorage.getItem('hok_products_cleared') === 'true';
    const deletedIds = (() => {
      try {
        return new Set(JSON.parse(localStorage.getItem('hok_deleted_products') || '[]'));
      } catch (e) {
        return new Set();
      }
    })();

    const defaultItems = isCleared ? [] : (collectionProducts[collId] || [])
      .filter(item => !deletedIds.has(item.id));

    const databaseItems = (products || [])
      .filter(p => p.collection === collName && !deletedIds.has(p.id));

    const total = databaseItems.length + defaultItems.filter(item => !databaseItems.some(x => String(x.id) === String(item.id))).length;
    return Math.max(1, Math.ceil(total / 8));
  };

  const handleAddToCart = (product) => {
    const liveProd = (products || []).find(p => String(p.id) === String(product.id));
    const maxStock = (liveProd && typeof liveProd.stock === 'number')
      ? liveProd.stock
      : (typeof product.stock === 'number' ? product.stock : 10);

    if (maxStock <= 0) {
      triggerStockLimitAlert(0);
      return;
    }

    let limitReached = false;
    setCartItems(prev => {
      const existing = prev.find(item => String(item.id) === String(product.id));
      const currentQty = existing ? existing.qty : 0;
      if (currentQty + 1 > maxStock) {
        limitReached = true;
        triggerStockLimitAlert(maxStock);
        if (existing) {
          return prev.map(item => String(item.id) === String(product.id) ? { ...item, stock: maxStock, qty: maxStock } : item);
        } else {
          return prev;
        }
      }
      if (existing) {
        return prev.map(item => String(item.id) === String(product.id) ? { ...item, stock: maxStock, qty: item.qty + 1 } : item);
      }
      return [...prev, { ...product, stock: maxStock, qty: 1 }];
    });

    if (!limitReached) {
      setAddedProductIds(prev => ({ ...prev, [product.id]: true }));
      setTimeout(() => {
        setAddedProductIds(prev => {
          const next = { ...prev };
          delete next[product.id];
          return next;
        });
      }, 2000);
    }
  };

  const [undoItem, setUndoItem] = useState(null);
  const undoTimerRef = useRef(null);

  const handleQtyChange = (id, delta) => {
    setCartItems(prev => {
      const item = prev.find(i => String(i.id) === String(id));
      if (!item) return prev;
      if (item.qty + delta <= 0) {
        if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
        setUndoItem(item);
        undoTimerRef.current = setTimeout(() => setUndoItem(null), 5000);
        return prev.filter(i => String(i.id) !== String(id));
      }
      const liveProd = (products || []).find(p => String(p.id) === String(id));
      const itemStock = (liveProd && typeof liveProd.stock === 'number')
        ? liveProd.stock
        : ((typeof item.stock === 'number') ? item.stock : 10);

      if (delta > 0 && item.qty + delta > itemStock) {
        triggerStockLimitAlert(itemStock);
        return prev.map(i => String(i.id) === String(id) ? { ...i, stock: itemStock, qty: itemStock } : i);
      }
      return prev.map(i => String(i.id) === String(id) ? { ...i, stock: itemStock, qty: i.qty + delta } : i);
    });
  };

  const removeItem = (id) => {
    const item = cartItems.find(i => i.id === id);
    if (item) {
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
      setUndoItem(item);
      undoTimerRef.current = setTimeout(() => setUndoItem(null), 5000);
    }
    setCartItems(prev => prev.filter(i => i.id !== id));
  };

  const handleUndo = () => {
    if (undoItem) {
      setCartItems(prev => [...prev, { ...undoItem, qty: 1 }]);
      setUndoItem(null);
      if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    }
  };

  const getSubtotal = () => {
    return cartItems.reduce((acc, item) => acc + (item.price * item.qty), 0);
  };



  return (
    <div className="bg-noise bg-fish-pattern min-h-screen relative font-sans text-[#584738] flex flex-col antialiased">
      <Header
        setView={setView}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        setIsSearchOpen={setIsSearchOpen}
        setIsAuthOpen={setIsAuthOpen}
        setIsCartOpen={setIsCartOpen}
        cartItems={cartItems}
      />

      {/* 2. HERO BANNER */}
      <section className="relative w-full h-[40vh] min-h-[260px] max-h-[480px] sm:h-[55vh] sm:max-h-none md:h-[80vh] flex items-center justify-center overflow-hidden border-b border-[#584738]/5">
        <img
          src={collectionsData[selectedCollectionId || 1]?.banner || "/blue_pottery_banner.jpg"}
          alt={`${collectionsData[selectedCollectionId || 1]?.title || "Collection"} Banner`}
          className="absolute inset-0 w-full h-full object-cover object-center sm:object-[center_35%]"
          fetchPriority="high"
          decoding="async"
        />
      </section>

      {/* 2.5 STICKY HORIZONTAL COLLECTION TABS */}
      <div id="collections-content" className="w-full bg-[#FAF8F5] border-b border-[#584738]/10 py-5 px-6 sticky top-0 z-30 overflow-x-auto scrollbar-none flex justify-start md:justify-center gap-6 md:gap-10">
        {[1, 2, 3, 4, 5, 6].map((idx) => (
          <button
            key={idx}
            onClick={() => setSelectedCollectionId(idx)}
            className={`whitespace-nowrap font-sans text-xs md:text-sm tracking-widest uppercase pb-1.5 focus:outline-none transition-all duration-300 ${
              (selectedCollectionId || 1) === idx
                ? 'border-b-2 border-[#584738] text-[#584738] font-bold'
                : 'text-[#584738]/50 hover:text-[#584738]/85 font-light'
            }`}
          >
            {collectionsData[idx].title}
          </button>
        ))}
      </div>

      {/* 3. PRODUCT GRID */}
      <section ref={productSectionRef} className="px-4 sm:px-6 py-10 md:py-12 max-w-7xl mx-auto w-full">
        {/* Collection details in Louis George Cafe Light font with the site bg pattern */}
        <div className="w-full pb-10 md:pb-14 text-center max-w-4xl mx-auto flex flex-col items-center animate-fade-in">
          <h2 className="text-[clamp(1.15rem,4.5vw,1.875rem)] sm:text-3xl md:text-5xl text-[#584738] font-serif font-normal tracking-wider md:tracking-widest uppercase mb-4 md:mb-5 whitespace-nowrap">
            {collectionsData[selectedCollectionId || 1]?.title}
          </h2>
          <p className="text-[14px] md:text-[15.5px] leading-[1.85] text-[#584738]/75 text-center tracking-wide font-light font-sans max-w-3xl">
            {collectionsData[selectedCollectionId || 1]?.description}
          </p>
          <div className="w-16 h-[0.75px] bg-[#584738]/15 mt-8"></div>
        </div>

        <div key={currentPage} className="grid grid-cols-2 gap-x-6 gap-y-12 md:gap-x-12 md:gap-y-16 max-w-5xl mx-auto animate-fade-in">
          {getPageProducts().map((product, idx) => {
            const liveProd = (products || []).find(p => String(p.id) === String(product.id)) || product;
            const currentStock = typeof liveProd.stock === 'number' ? liveProd.stock : (typeof product.stock === 'number' ? product.stock : 10);
            const isOutOfStock = currentStock <= 0;

            return (
              <div key={`${product.id}-${idx}-${currentPage}`} className="group flex flex-col relative animate-fade-in">
                {/* Image Container with Elegant Hover Effects */}
                <div 
                  onClick={() => { setSelectedProduct(liveProd); setView('product'); }}
                  className="relative aspect-square sm:aspect-[4/5] overflow-hidden border border-[#584738]/5 shadow-xs mb-3.5 bg-[#FAF8F5] flex items-center justify-center cursor-pointer"
                >
                  {isOutOfStock && (
                    <div className="absolute top-2 right-2 z-10 bg-[#584738]/90 text-[#F5E6D8] text-[9px] font-sans font-bold tracking-wider px-2 py-0.5 uppercase shadow-sm">
                      Out of Stock
                    </div>
                  )}
                  <img
                    src={product.image}
                    alt={product.name}
                    className={`w-full h-full object-cover transform scale-100 group-hover:scale-[1.03] transition-transform duration-700 ease-out ${isOutOfStock ? 'opacity-75 grayscale' : ''}`}
                    loading="lazy"
                    decoding="async"
                  />
                  
                  {/* Modern luxury quick action overlay */}
                  <div className="absolute inset-0 bg-black/15 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center pointer-events-none group-hover:pointer-events-auto">
                    {isOutOfStock ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); triggerStockLimitAlert(0); }}
                        className="bg-neutral-400 text-white cursor-not-allowed px-6 py-2.5 text-[11px] tracking-[0.2em] font-sans font-medium uppercase shadow-md transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500 ease-out"
                      >
                        Out of Stock
                      </button>
                    ) : (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleAddToCart(liveProd); }}
                        disabled={addedProductIds[product.id]}
                        className={`px-6 py-2.5 text-[11px] tracking-[0.2em] font-sans font-medium uppercase transition-all duration-300 shadow-md transform translate-y-2 group-hover:translate-y-0 transition-transform duration-500 ease-out flex items-center justify-center gap-1.5 ${
                          addedProductIds[product.id]
                            ? 'bg-[#3E5C43] text-white cursor-default'
                            : 'bg-[#584738] text-[#F5E6D8] hover:bg-black'
                        }`}
                      >
                        {addedProductIds[product.id] ? (
                          <>
                            <svg className="w-3.5 h-3.5 stroke-current animate-bounce" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                            <span>Added to Bag</span>
                          </>
                        ) : (
                          <span>Add to Bag</span>
                        )}
                      </button>
                    )}
                  </div>
                </div>

                {/* Product Label Info */}
                <div className="flex justify-between items-center text-xs md:text-sm font-sans tracking-wider text-[#584738] px-1">
                  <div className="flex flex-col">
                    <span 
                      onClick={() => { setSelectedProduct(liveProd); setView('product'); }}
                      className="font-light cursor-pointer hover:underline transition-all duration-200"
                    >
                      {product.name}
                    </span>
                    {isOutOfStock && (
                      <span className="text-[10px] text-[#8C3E3E] font-medium tracking-wide uppercase mt-0.5">
                        Out of Stock
                      </span>
                    )}
                  </div>
                  <span className="font-medium text-[#584738]/90">
                    ₹{product.price.toLocaleString('en-IN')}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Elegant Pagination Section */}
        <div className="mt-14 flex items-center justify-center gap-5 select-none font-sans">
          {/* Previous Arrow Button */}
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className={`p-1.5 rounded-full border border-[#584738]/20 transition-all duration-300 flex items-center justify-center ${
              currentPage === 1 
                ? 'opacity-30 cursor-not-allowed text-[#584738]/40' 
                : 'hover:bg-[#584738] hover:text-[#F5E6D8] text-[#584738] active:scale-95 cursor-pointer'
            }`}
            aria-label="Previous Page"
          >
            <svg className="w-3.5 h-3.5 stroke-current" fill="none" viewBox="0 0 24 24" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Dots indicators */}
          <div className="flex items-center gap-2.5">
            {Array.from({ length: getPageCount() }, (_, i) => i + 1).map((pageNum) => (
              <button
                key={pageNum}
                onClick={() => handlePageChange(pageNum)}
                className="relative group flex items-center justify-center p-1 focus:outline-none cursor-pointer"
                aria-label={`Go to page ${pageNum}`}
              >
                {/* Outer Ring Effect for Active Dot */}
                <span
                  className={`absolute w-4 h-4 rounded-full border border-[#584738] transition-all duration-300 scale-0 ${
                    currentPage === pageNum ? 'scale-100 opacity-60' : 'group-hover:scale-75 group-hover:opacity-30'
                  }`}
                />
                {/* Inner Solid Dot */}
                <span
                  className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                    currentPage === pageNum 
                      ? 'bg-[#584738] scale-110 shadow-xs' 
                      : 'bg-[#584738]/30 group-hover:bg-[#584738]/70 hover:scale-105'
                  }`}
                />
              </button>
            ))}
          </div>

          {/* Next Arrow Button */}
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === getPageCount()}
            className={`p-1.5 rounded-full border border-[#584738]/20 transition-all duration-300 flex items-center justify-center ${
              currentPage === getPageCount()
                ? 'opacity-30 cursor-not-allowed text-[#584738]/40' 
                : 'hover:bg-[#584738] hover:text-[#F5E6D8] text-[#584738] active:scale-95 cursor-pointer'
            }`}
            aria-label="Next Page"
          >
            <svg className="w-3.5 h-3.5 stroke-current" fill="none" viewBox="0 0 24 24" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </section>

      {/* 4. NEWSLETTER SIGNUP SECTION */}
      <section className="w-full bg-[#CEC1A8] py-10 px-8 md:py-12 md:px-20 border-t border-[#584738]/10">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-8 md:gap-16">
          <div className="flex flex-col text-left">
            <span className="text-[12px] md:text-[13px] tracking-[0.18em] text-[#584738] font-sans font-medium mb-3 block">
              Sign Up To Our Newsletter
            </span>
            <h2 className="text-2xl md:text-4.5xl font-serif text-[#584738] font-medium leading-tight">
              Be a part of <span className="text-serif-italic text-3xl md:text-5.5xl font-semibold lowercase">our world!</span>
            </h2>
          </div>

          <div className="w-full md:w-auto flex-1 max-w-lg">
            <form
              onSubmit={(e) => { e.preventDefault(); alert("Subscribed to the newsletter!"); }}
              className="flex flex-col sm:flex-row gap-3 items-stretch w-full"
            >
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 bg-transparent border border-[#584738]/45 px-4 py-3.5 text-sm text-[#584738] placeholder-[#584738]/70 focus:outline-none focus:border-[#584738] font-sans font-normal"
                required
              />
              <button
                type="submit"
                className="bg-[#584738] text-white hover:bg-black px-8 py-3.5 text-[11px] tracking-[0.2em] font-sans font-bold uppercase transition-colors"
              >
                SUBSCRIBE
              </button>
            </form>
            <p className="text-[10.5px] text-[#584738]/80 font-sans font-medium tracking-wide mt-2">
              By signing up you agree with our Terms & Conditions
            </p>
          </div>
        </div>
      </section>

      {/* 5. FOOTER */}
      <Footer setView={setView} setIsBookingOpen={setIsBookingOpen} />

      {/* --- DRAWERS & MODALS SYNCED WITH STATE --- */}

      {/* A. MOBILE MENU DRAWER */}
      <MenuDrawer isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} setView={setView} setIsBookingOpen={setIsBookingOpen} setSelectedCollectionId={setSelectedCollectionId} currentUser={currentUser} setCurrentUser={setCurrentUser} setIsAuthOpen={setIsAuthOpen} />
      <AuthModal isOpen={isAuthOpen} onClose={() => setIsAuthOpen(false)} currentUser={currentUser} setCurrentUser={setCurrentUser} />

      {/* B. LUXURY CART SLIDE-OVER */}
<div
  className={`fixed inset-0 z-50 transition-opacity duration-300 pointer-events-none ${
    isCartOpen ? "opacity-100 pointer-events-auto" : "opacity-0"
  }`}
>
  {/* Backdrop */}
  <div
    onClick={() => setIsCartOpen(false)}
    className="absolute inset-0 bg-black/40 backdrop-blur-xs"
  />

  {/* Cart Drawer */}
  <div
    className={`fixed inset-y-0 right-0 h-full w-full md:w-96 bg-noise shadow-2xl p-6 transition-transform duration-300 flex flex-col ${
      isCartOpen ? "translate-x-0" : "translate-x-full"
    }`}
  >
    <div className="flex items-center justify-between border-b border-[#584738]/10 pb-4 mb-6 flex-shrink-0">
      <h3 className="font-serif text-lg tracking-widest font-semibold uppercase flex items-center gap-2 text-[#584738]">
        Your Cart
        <span className="text-xs bg-[#584738]/10 text-[#584738] px-2.5 py-0.5 rounded-full font-sans font-bold">
          {cartItems.reduce((acc, it) => acc + it.qty, 0)}
        </span>
      </h3>

      <button
        onClick={() => setIsCartOpen(false)}
        className="text-[#584738] hover:text-[#584738] focus:outline-none"
        aria-label="Close Cart"
      >
        <CloseIcon />
      </button>
    </div>

    <div className="flex-1 min-h-0 overflow-y-auto space-y-5 pr-1 scrollbar-thin">
      {cartItems.length === 0 ? (
        <div className="h-full flex flex-col items-center justify-center text-center p-6 text-[#584738]/40 font-serif">
          <CartIcon />
          <p className="mt-3 text-sm tracking-wide font-medium">
            Your cart is empty.
          </p>

          <button
            onClick={() => setIsCartOpen(false)}
            className="text-[#584738] hover:underline text-xs tracking-widest font-semibold uppercase mt-2"
          >
            Start Shopping
          </button>
        </div>
      ) : (
        cartItems.map((item, index) => (
          <div
            key={`${item.id}-${index}`}
            className="flex gap-4 border-b border-[#584738]/5 pb-4 font-serif"
          >
            <img
              src={item.image}
              alt={item.name}
              className="w-16 h-20 object-cover border border-[#584738]/5"
            />

            <div className="flex-1 flex flex-col justify-between">
              <div>
                <h4 className="text-sm font-serif font-medium tracking-wide text-[#584738]/90 leading-tight">
                  {item.name}
                </h4>

                <p className="text-[12.5px] text-[#584738] font-medium mt-1">
                  ₹{item.price.toLocaleString("en-IN")} <span className="text-[9.5px] text-[#584738]/60 font-sans font-normal">(incl. of all taxes)</span>
                </p>
              </div>

              <div className="flex items-center justify-between text-xs mt-2 font-sans">
                <div className="flex items-center border border-[#584738]/20 select-none">
                  <button
                    onClick={() => handleQtyChange(item.id, -1)}
                    className="px-2 py-0.5 hover:bg-[#584738]/5 focus:outline-none"
                  >
                    -
                  </button>

                  <span className="px-3 py-0.5 text-center font-medium bg-[#584738]/2.5">
                    {item.qty}
                  </span>

                  <button
                    onClick={() => handleQtyChange(item.id, 1)}
                    className="px-2 py-0.5 hover:bg-[#584738]/5 focus:outline-none"
                  >
                    +
                  </button>
                </div>

                <button
                  onClick={() => removeItem(item.id)}
                  className="text-[#584738]/60 hover:text-red-500 font-semibold"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        ))
      )}
    </div>

    {cartItems.length > 0 && (
      <div className="border-t border-[#584738]/10 pt-6 mt-6 flex flex-col gap-4 flex-shrink-0">
        <div className="flex items-center justify-between font-serif">
          <div className="flex flex-col text-left">
            <span className="text-md font-medium tracking-wide text-[#584738]/80">
              Subtotal
            </span>
            <span className="text-[10px] text-[#584738]/60 font-sans normal-case">
              (Inclusive of all taxes)
            </span>
          </div>

          <span className="text-lg font-bold text-[#584738]">
            ₹{getSubtotal().toLocaleString("en-IN")}
          </span>
        </div>

        <p className="text-[10px] text-[#584738]/60 tracking-wider text-center font-sans">
          Complimentary Express Shipping • (Inclusive of all taxes)
        </p>

        <button
          onClick={() => {
            setView('checkout');
            setIsCartOpen(false);
          }}
          className="w-full bg-[#584738] text-white hover:bg-black py-3.5 text-xs tracking-widest font-bold uppercase transition-colors shadow-xs font-sans"
        >
          PROCEED TO CHECKOUT
        </button>
      </div>
    )}

    {/* ── UNDO TOAST ── */}
    {undoItem && (
      <div className="flex-shrink-0 mt-3 flex items-center justify-between gap-3 bg-[#584738] text-[#F5E6D8] px-4 py-3 rounded-xs animate-fade-in">
        <p className="text-[11.5px] font-sans tracking-wide truncate flex-1">
          <span className="opacity-60 mr-1">Removed:</span>{undoItem.name}
        </p>
        <button
          onClick={handleUndo}
          className="flex items-center gap-1.5 text-[11px] font-sans font-bold tracking-widest uppercase text-[#CEC1A8] hover:text-white transition-colors focus:outline-none flex-shrink-0"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 15L3 9m0 0l6-6M3 9h12a6 6 0 010 12h-3" />
          </svg>
          Undo
        </button>
      </div>
    )}
  </div>
</div>

      {/* C. BOOK SALON APPOINTMENT PANEL */}
      {/* C. BOOK SALON APPOINTMENT PANEL */}
      <BookingDrawer
        isOpen={isBookingOpen}
        onClose={() => { setIsBookingOpen(false); setBookingStep(1); }}
        bookingStep={bookingStep}
        setBookingStep={setBookingStep}
        bookingForm={bookingForm}
        setBookingForm={setBookingForm}
      />

    </div>
  );
}
